import { RunCircle } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Alert, Box, Button, Stack } from "@mui/material";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import EditableDataGrid from "@poseidon/ui/components/EditableDataGrid";
import { Panel } from "@poseidon/ui/components/Panel";
import { usePermissions } from "@poseidon/ui/components";
import { useCallback, useEffect, useState } from "react";
import * as api from "../../api";
import { useAffordabilityReport } from "../../hooks/useAffordabilityReport";
import { useRunAffordabilityReport } from "../../hooks/useRunAffordabilityReport";
import { isApplicationEditable } from "../../isApplicationEditable";
import { affordabilityColumns } from "./affordabilityColumnDefinition";
import {
  AffordabilityGridRow,
  currentMonthlyCommitmentSection,
  incomeSection,
  monthlyLivingCostSection,
} from "./affordabilitySections";
import { AffordabilitySection, Subset } from "./affordabilityState";

const getIncomeSection = (as: Subset<AffordabilitySection>) => {
  return as?.income ?? {};
};
const getCurrentMonthlyCommitmentSection = (
  as: Subset<AffordabilitySection>
) => {
  return as?.currentMonthlyCommitments ?? {};
};
const getMontlyLivingCostSection = (as: Subset<AffordabilitySection>) => {
  return as?.monthlyLivingCosts ?? {};
};

const getUpdateObject = (
  updatedRow: AffordabilityGridRow,
  previous: Subset<AffordabilitySection>
): Subset<AffordabilitySection> => {
  const { key, manual } = updatedRow;
  if (incomeSection.includes(key)) {
    return {
      ...previous,
      income: {
        ...getIncomeSection(previous),
        [key]: !manual && manual !== 0 ? 0 : manual,
      },
    };
  }
  if (currentMonthlyCommitmentSection.includes(key)) {
    return {
      ...previous,
      currentMonthlyCommitments: {
        ...getCurrentMonthlyCommitmentSection(previous),
        [key]: !manual && manual !== 0 ? 0 : manual,
      },
    };
  }
  if (monthlyLivingCostSection.includes(key)) {
    return {
      ...previous,
      monthlyLivingCosts: {
        ...getMontlyLivingCostSection(previous),
        [key]: !manual && manual !== 0 ? 0 : manual,
      },
    };
  }
  return {
    ...previous,
  };
};

export const AffordabilityCalculator = ({
  application,
}: {
  application: any;
}) => {
  const { canWrite } = usePermissions();
  const { setToast, setWarningToast } = useToastMessage();
  const { setError } = useErrorMessage();
  const {
    runReport,
    loading: runLoading,
    error: runError,
    success: runSuccess,
  } = useRunAffordabilityReport(application.id);

  const { affordabilityReport, gridRows, loading, error } =
    useAffordabilityReport(application.id);

  const [manualReport, setManualReport] = useState<
    Subset<AffordabilitySection>
  >(affordabilityReport?.manual || {});

  const onSaveChangesClick = useCallback(async () => {
    await api.updateAffordabilityReport(application.id, manualReport);
    setManualReport({});
    setToast("Updated Affordability Report");
  }, [application.id, manualReport, setToast]);

  const readonly = !isApplicationEditable(application);

  const isEditInvalid = (newRow: AffordabilityGridRow) => {
    if (typeof newRow?.manual === "undefined") {
      return "Unknown error";
    }
    if (
      newRow["rowType"] !== "inner" ||
      newRow["section"] === "workings" ||
      newRow["section"] === "newBoatCosts"
    ) {
      return "Cannot update this cell";
    }

    const valueStr = newRow?.manual?.toString();
    const validNumber = !isNaN(parseInt(valueStr));

    if (!validNumber && valueStr !== "") {
      return "Update rejected: Cell only accepts numeric values";
    }
    return false;
  };

  const processRowUpdate = useCallback(
    (newRow: AffordabilityGridRow, oldRow: AffordabilityGridRow) => {
      const error = isEditInvalid(newRow);
      if (error) {
        setWarningToast(error);
        return oldRow;
      }

      setManualReport((prev) => {
        return getUpdateObject(newRow, prev);
      });

      return newRow;
    },
    [setManualReport, setWarningToast]
  );

  const onProcessRowUpdateError = (error: string) => setError(error);

  useEffect(() => {
    if (runError) {
      setError(runError.toString());
    }

    if (error) {
      setError(error);
    }
    if (runSuccess) {
      setToast("Successfully run affordability report");
    }
  }, [error, runError, runSuccess, setError, setToast]);

  const dirty = !!Object.entries(manualReport || {}).length;
  return (
    <Panel expandable title="Affordability calculator" sx={{ width: 1 }}>
      {gridRows.length <= 0 ? (
        <>
          <Alert severity="warning">No affordability report generated.</Alert>
          <Stack spacing={2} mt={5} mb={2} alignItems="center">
            <LoadingButton
              onClick={runReport}
              loading={runLoading}
              loadingPosition="start"
              startIcon={<RunCircle />}
              variant="contained"
              color="primary"
              size="small"
              disabled={readonly || !canWrite}
              sx={{ mb: 3, maxWidth: 280 }}
            >
              Generate Affordability Report
            </LoadingButton>
          </Stack>
        </>
      ) : (
        <Stack>
          <Box
            sx={{
              width: 1,
            }}
          >
            <EditableDataGrid
              loading={loading || runLoading}
              autoHeight={true}
              rows={gridRows}
              columns={affordabilityColumns}
              columnVisibilityModel={{ id: false, key: false }}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={onProcessRowUpdateError}
              getRowClassName={(params: {
                row: { key: any; rowType: any; section: any };
              }) => {
                return `affordability-calculator-${params.row.key} affordability-calculator__row--${params.row.rowType} affordability-calculator__row--${params.row.section}`;
              }}
              isCellEditable={({ row }: any) =>
                row.rowType === "inner" &&
                row.section !== "workings" &&
                row.section !== "newBoatCosts"
              }
              pageSize={20}
              rowHeight={25}
              readonly={readonly}
              getRowId={(row: { key: any }) => row.key}
              disableColumnFilter={true}
              disableColumnSelector={true}
              disableColumnMenu={true}
              hideFooter={true}
              rowStriping={false}
              rowStyles={{
                "&.affordability-calculator__row--heading": {
                  minHeight: "30px",
                  ".MuiDataGrid-cell": {
                    minHeight: "30px!important",
                  },
                  backgroundColor: "neutral.200",
                  fontWeight: "bold",
                },
                "&.affordability-calculator__row--total": {
                  backgroundColor: "neutral.100",
                  minHeight: "30px",
                  fontWeight: "medium",
                  ".MuiDataGrid-cell": {
                    minHeight: "30px!important",
                  },
                  ".MuiDataGrid-cell:first-of-type": {
                    paddingLeft: "24px",
                  },
                },
                "&.affordability-calculator__row--inner": {
                  ".MuiDataGrid-cell:first-of-type": {
                    paddingLeft: "24px",
                  },
                },
                "&.affordability-calculator__row--result": {
                  backgroundColor: "neutral.200",
                  fontWeight: "bold",
                },
                "&.affordability-calculator__row--workings": {
                  ".MuiDataGrid-cell": {
                    fontStyle: "italic",
                  },
                },
              }}
            />
          </Box>
          <Stack
            my={2}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={4}
          >
            <LoadingButton
              onClick={runReport}
              loading={runLoading}
              loadingPosition="start"
              startIcon={<RunCircle />}
              variant="contained"
              color="primary"
              size="small"
              disabled={readonly || dirty || !canWrite}
            >
              Re-run Affordability Report
            </LoadingButton>
            {dirty && !readonly && canWrite && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={onSaveChangesClick}
              >
                Save Changes
              </Button>
            )}
          </Stack>
        </Stack>
      )}
      {!(application.lock_sailor || application.lock_personalDetails) && (
        <Alert severity="warning" sx={{ width: 1 }}>
          Warning: The application is not locked and the sailor can still edit
          these fields. You should not rely on the results of this report unless
          it is generated after the application is locked
        </Alert>
      )}
    </Panel>
  );
};
